import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import RegisterCar from "./pages/RegisterCar";
import { ProductionGuard } from "./components/ProductionGuard";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { runtimeMode } from "./lib/runtimeMode";

const Layout = lazy(() => import("./components/Layout").then((module) => ({ default: module.Layout })));
const StaffRoute = lazy(() => import("./components/StaffRoute").then((module) => ({ default: module.StaffRoute })));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NewSale = lazy(() => import("./pages/NewSale"));
const Scan = lazy(() => import("./pages/Scan"));
const Customers = lazy(() => import("./pages/Customers"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const ServiceTickets = lazy(() => import("./pages/ServiceTickets"));
const ServiceTicketDetail = lazy(() => import("./pages/ServiceTicketDetail"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const VehicleDetail = lazy(() => import("./pages/VehicleDetail"));
const Customer360 = lazy(() => import("./pages/Customer360"));
const Suppliers = lazy(() => import("./pages/Suppliers"));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders"));
const Reminders = lazy(() => import("./pages/Reminders"));
const Retention = lazy(() => import("./pages/Retention"));
const FollowUp = lazy(() => import("./pages/FollowUp"));
const DailySummary = lazy(() => import("./pages/DailySummary"));
const Reports = lazy(() => import("./pages/Reports"));
const SystemCheck = lazy(() => import("./pages/SystemCheck"));
const Settings = lazy(() => import("./pages/Settings"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CounterMode = lazy(() => import("./pages/CounterMode"));
const CustomerIntake = lazy(() => import("./pages/CustomerIntake"));
const RoleLanding = lazy(() => import("./components/RoleLanding").then((module) => ({ default: module.RoleLanding })));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="grid min-h-screen place-items-center bg-background p-4 text-sm font-medium text-muted-foreground">
    Loading...
  </div>
);

const AppRoutes = () => (
  <BrowserRouter>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Standalone customer QR surface. No staff shell or OS navigation. */}
        <Route path="/customer/register-car/:shopId?" element={<RegisterCar />} />
        <Route path="/register-car/:shopId?" element={<RegisterCar />} />

        {/* Staff authentication surface. Staff accounts are provisioned outside the public app. */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signup" element={<Navigate to="/login" replace />} />
        <Route path="/onboarding" element={<Navigate to="/login" replace />} />

        {/* Every operational route requires an active staff profile. */}
        <Route element={<StaffRoute />}>
          {/* Scan is full-screen, no chrome */}
          <Route path="/scan" element={<Scan />} />
          <Route path="/" element={<Layout><RoleLanding /></Layout>} />
          <Route path="/sale" element={<Layout><NewSale /></Layout>} />
          <Route path="/stock" element={<Layout><Inventory /></Layout>} />
          <Route path="/customers" element={<Layout><Customers /></Layout>} />
          <Route path="/customers/:id" element={<Layout><Customer360 /></Layout>} />
          <Route path="/vehicles/:id" element={<Layout><Customer360 /></Layout>} />
          <Route path="/invoices" element={<Layout><Invoices /></Layout>} />
          <Route path="/invoices/:id" element={<Layout><InvoiceDetail /></Layout>} />
          <Route path="/jobs" element={<Layout><ServiceTickets /></Layout>} />
          <Route path="/jobs/:id" element={<Layout><ServiceTicketDetail /></Layout>} />
          <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
          <Route path="/counter" element={<Layout><CounterMode /></Layout>} />
          <Route path="/customer-intake" element={<Layout><CustomerIntake /></Layout>} />
          <Route path="/follow-up" element={<Layout><FollowUp /></Layout>} />
          <Route path="/reports" element={<Layout><Reports /></Layout>} />

          {/* Backwards-compat / less-used */}
          <Route path="/pos" element={<Layout><NewSale /></Layout>} />
          <Route path="/inventory" element={<Layout><Inventory /></Layout>} />
          <Route path="/suppliers" element={<Layout><Suppliers /></Layout>} />
          <Route path="/purchase-orders" element={<Layout><PurchaseOrders /></Layout>} />
          <Route path="/reminders" element={<Layout><Reminders /></Layout>} />
          <Route path="/retention" element={<Layout><Retention /></Layout>} />
          <Route path="/daily-summary" element={<Layout><DailySummary /></Layout>} />
          <Route path="/system-check" element={<Layout><SystemCheck /></Layout>} />
          <Route path="/settings" element={<Layout><Settings /></Layout>} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </BrowserRouter>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {runtimeMode.isProductionWithoutSupabase ? (
          <ProductionGuard />
        ) : (
          <AppRoutes />
        )}
        <Toaster />
        <Sonner />
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
